import React, { memo } from "react";
import { UserChatMessage, UserChatMessageRef } from "../../../components/UserChatMessage";
import { AnswerLoading, AnswerError } from "../../../components/Answer";
import { AnswerRegenarate } from "../../../components/Answer/AnswerRegenarate";
import { AnalysisPanelTabs } from "../../../components/AnalysisPanel";
import { AppType } from "../../../api/models";
import { Histories } from "../../../api";
import styles from "../Chat.module.css";

export interface ChatMessageListProps {
    answers: any[];
    selectedAnswer: number;
    activeAnalysisPanelTab: AnalysisPanelTabs | undefined;
    selectedAppType: AppType;
    isLoading: boolean;
    isStreaming: boolean;
    IsOnEdit: boolean;
    isAdmin: boolean;
    selectedQuestion: number;
    applyRestriction: boolean;
    searchTerm: string;
    versionIndexMap: Record<string, number>;
    histories: Histories;
    doctype: boolean;
    error: unknown;
    isRegenerateActive: React.MutableRefObject<boolean>;

    // Refs
    currentQuestionInfo: React.MutableRefObject<any>;
    userChatMessageRefs: React.MutableRefObject<{ [key: number]: UserChatMessageRef | null }>;
    chatResponseAreaRefValue: React.MutableRefObject<HTMLDivElement | null>;
    answersRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
    isTabClick: React.MutableRefObject<string>;
    lastAnswerIndexRef: React.MutableRefObject<number>;
    currentTargetQuestionId: React.MutableRefObject<number>;
    lastQuestionRef: any;
    answerAreaRef: React.RefObject<HTMLDivElement | null>;
    chatMessageStreamEnd: React.RefObject<HTMLDivElement | null>;

    // Callbacks
    onShowCitation: (citation: string, citationIndex: number, answerIndex: number) => void;
    onToggleTab: (tab: AnalysisPanelTabs, index: number) => void;
    makeApiRequest: (
        question: string,
        extractedQuestion: string,
        appType: AppType,
        combinedAppTypeResponse: boolean,
        fileAttachments?: any[],
        regenerated?: boolean,
        docType?: string,
        selectedChatIndex?: number,
        isEdit?: boolean,
        templateId?: string,
        answer?: any
    ) => void;
    onEditClick: (checked: boolean, index: number) => void;
    setSelectedAppType: (appType: AppType) => void;
    setOverAllCurrentSessionDataPoints: (answers: any[]) => void;
    setAnswers: (answers: any[]) => void;
    onFeedbackSubmitClicked: (feedback: any, conv_id: string, appType: AppType) => void;
    applyVersionChange: (versionIndex: number, parentMessageId: string, answerIndex: number) => void;
    setSelectedAnswer: (index: number) => void;
    setSelectedCitation: (index: number) => void;
    dispatch: any;
    QuestionInputReducerAction: any;
    renderUnifiedAnswer: (answer: any, questionDetails: any, index: number, isStreaming: boolean, isHistory: boolean) => React.JSX.Element;
    setRefsForAnswersRender: (node: HTMLDivElement | null, index: number) => void;
    expandedQAIndex: number | null;
    onExpandQA: (index: number) => void;
    clearActiveAnalysisPanelTab?: () => void;
}

const ChatMessageListComponent: React.FC<ChatMessageListProps> = ({
    answers,
    selectedAnswer,
    activeAnalysisPanelTab,
    selectedAppType,
    isLoading,
    isStreaming,
    IsOnEdit,
    isAdmin,
    selectedQuestion,
    applyRestriction,
    searchTerm,
    versionIndexMap,
    histories,
    doctype,
    error,
    isRegenerateActive,
    currentQuestionInfo,
    userChatMessageRefs,
    chatResponseAreaRefValue,
    answersRefs,
    isTabClick,
    lastAnswerIndexRef,
    currentTargetQuestionId,
    lastQuestionRef,
    answerAreaRef,
    chatMessageStreamEnd,
    onShowCitation,
    onToggleTab,
    makeApiRequest,
    onEditClick,
    setSelectedAppType,
    setOverAllCurrentSessionDataPoints,
    setAnswers,
    onFeedbackSubmitClicked,
    applyVersionChange,
    setSelectedAnswer,
    setSelectedCitation,
    dispatch,
    QuestionInputReducerAction,
    renderUnifiedAnswer,
    setRefsForAnswersRender,
    expandedQAIndex,
    onExpandQA,
    clearActiveAnalysisPanelTab
}) => {
    const requestMissingResponseForAppType = (answerRow: any, index: number, appType: AppType, requestTriggerType: "RegenerateClick" | "TabSwitch") => {
        dispatch(QuestionInputReducerAction.updateRequestTriggerType(requestTriggerType));
        lastAnswerIndexRef.current = index;
        currentTargetQuestionId.current = index;
        isTabClick.current = "true";
        setSelectedAnswer(index);
        setSelectedCitation(0);
        clearActiveAnalysisPanelTab?.();
        currentQuestionInfo.current = null;
        makeApiRequest(
            answerRow[0].question,
            answerRow[0].extractedQuestion,
            appType,
            appType === "ALL",
            answerRow[0].filesAttached,
            undefined,
            undefined,
            undefined,
            undefined,
            answerRow[0].templateId,
            answerRow[1]
        );
        setSelectedAnswer(index);
    };

    return (
        <div className={styles.chatMessageInputDiv}>
            <div ref={answerAreaRef} className={styles.chatMessageStream}>
                {Array.isArray(answers) &&
                    answers.map((answer, index) => (
                        <div key={index}>
                            <UserChatMessage
                                ref={el => {
                                    userChatMessageRefs.current[index] = el;
                                }}
                                key={index}
                                message={answer[0].extractedQuestion}
                                onSend={(question, appType: string) => {
                                    isTabClick.current = "false";
                                    dispatch(QuestionInputReducerAction.updateRequestTriggerType("QuestionEdit"));
                                    makeApiRequest(
                                        question,
                                        question,
                                        currentTargetQuestionId.current === 0 ? "ALL" : selectedAppType,
                                        (currentTargetQuestionId.current === 0 ? "ALL" : selectedAppType) === "ALL",
                                        answer[0].filesAttached,
                                        undefined,
                                        undefined,
                                        index,
                                        IsOnEdit,
                                        answer[0].templateId,
                                        answer[1]
                                    );
                                    currentQuestionInfo.current = null;
                                }}
                                index={index}
                                answers={answers}
                                onEditClick={(value: boolean, questionInfo: any) => {
                                    onEditClick(value, index);
                                    if (value) {
                                        currentQuestionInfo.current = questionInfo;
                                    } else {
                                        currentQuestionInfo.current = null;
                                    }
                                }}
                                IsOnEdit={IsOnEdit}
                                isAdminUser={isAdmin}
                                selectedQuestion={selectedQuestion === index}
                                applyRestriction={applyRestriction}
                                reference={chatResponseAreaRefValue}
                                responseGenerationActive={isLoading || isStreaming}
                                selectedAppType={selectedAppType}
                                templateId={answer[0].templateId}
                                questionContent={answer[0].question}
                                sourcePage={"chat"}
                                fileAttachments={answer[0].filesAttached}
                                searchTerm={searchTerm}
                                setSelectedAppType={setSelectedAppType}
                                currentVersionIndex={
                                    versionIndexMap[
                                        (answer[0]?.parentMessageId && answer[0].parentMessageId !== "0" ? answer[0].parentMessageId : answer[0].question_id) ??
                                            ""
                                    ] || 0
                                }
                                onVersionChange={(versionIndex: number, parentMessageId: string, answerIndex: number) => {
                                    applyVersionChange(versionIndex, parentMessageId, answerIndex);
                                }}
                                histories={histories}
                                onExpandToggle={!(isStreaming && index === answers.length - 1) ? () => onExpandQA(index) : undefined}
                                isExpanded={expandedQAIndex === index}
                            />
                            <div className={styles.chatMessageGpt} ref={element => setRefsForAnswersRender(element, index)}>
                                {(answer[1][selectedAppType] === undefined || answer[1][selectedAppType] === null) &&
                                !isLoading &&
                                !isStreaming &&
                                Object.keys(answer[1]).length > 1 ? (
                                    <div className={styles.chatMessageGptMinWidth}>
                                        <AnswerRegenarate
                                            selectedAppType={selectedAppType}
                                            isLoading={isLoading}
                                            isStreaming={isStreaming}
                                            answer={answer[1]}
                                            conversationDate={answer[0]?.conversation_date}
                                            onRetry={() => requestMissingResponseForAppType(answer, index, selectedAppType, "RegenerateClick")}
                                            onGenerateMissingResponse={(targetAppType: AppType) =>
                                                requestMissingResponseForAppType(answer, index, targetAppType, "TabSwitch")
                                            }
                                            setSelectedAppType={setSelectedAppType}
                                            onTabClicked={() => {
                                                currentTargetQuestionId.current = index;
                                                setSelectedAnswer(index);
                                                setSelectedCitation(0);
                                                sessionStorage.setItem("isTabClicked", "true");
                                                clearActiveAnalysisPanelTab?.();
                                            }}
                                            doctype={doctype}
                                            primaryId={index.toString()}
                                            isSelected={selectedAnswer === index && activeAnalysisPanelTab !== undefined}
                                        />
                                    </div>
                                ) : (answer[1][selectedAppType]?.context?.keywords !== undefined &&
                                      answer[1][selectedAppType]?.context?.feedbackgiven === false) ||
                                  answer[1][selectedAppType]?.context?.feedback?.category === 0 ||
                                  (!answer[1][selectedAppType] &&
                                      answer[1]["ALL"]?.context?.keywords !== undefined &&
                                      answer[1]["ALL"]?.context?.feedbackgiven === false) ||
                                  answer[1]["ALL"]?.context?.feedback?.category === 0 ? (
                                    renderUnifiedAnswer(answer, answer[0], index, isStreaming, false)
                                ) : (
                                    renderUnifiedAnswer(answer, answer[0], index, isStreaming, true)
                                )}
                            </div>
                        </div>
                    ))}

                {isLoading && isTabClick.current === "false" && !isRegenerateActive.current && (
                    <>
                        <UserChatMessage
                            message={lastQuestionRef.current.extractedQuestion}
                            onEditClick={value => onEditClick(value, 0)}
                            responseGenerationActive={isLoading || isStreaming}
                            selectedAppType={selectedAppType}
                            templateId={lastQuestionRef.current.templateId}
                            questionContent={lastQuestionRef.current.question}
                            sourcePage={"chat"}
                            isAdminUser={isAdmin}
                            searchTerm={""}
                            fileAttachments={lastQuestionRef.current.filesAttached}
                            setSelectedAppType={setSelectedAppType}
                            currentVersionIndex={0}
                        />
                        <div className={styles.chatMessageGptMinWidth}>
                            <AnswerLoading selectedAppType={selectedAppType} questionFilesAttached={lastQuestionRef.current.filesAttached} />
                        </div>
                    </>
                )}

                {error
                    ? isTabClick.current === "false" && (
                          <>
                              <UserChatMessage
                                  message={lastQuestionRef.current.extractedQuestion}
                                  onEditClick={value => onEditClick(value, 0)}
                                  responseGenerationActive={isLoading || isStreaming}
                                  selectedAppType={selectedAppType}
                                  templateId={lastQuestionRef.current.templateId}
                                  questionContent={lastQuestionRef.current.question}
                                  sourcePage={"chat"}
                                  isAdminUser={isAdmin}
                                  searchTerm={""}
                                  fileAttachments={lastQuestionRef.current.filesAttached}
                                  setSelectedAppType={setSelectedAppType}
                                  currentVersionIndex={0}
                              />
                              <div className={styles.chatMessageGptMinWidth}>
                                  <AnswerError
                                      error={error?.toString()}
                                      onRetry={() => {
                                          currentTargetQuestionId.current = 0;
                                          currentQuestionInfo.current = null;
                                          makeApiRequest(
                                              lastQuestionRef.current.question,
                                              lastQuestionRef.current.extractedQuestion,
                                              selectedAppType,
                                              selectedAppType === "ALL",
                                              lastQuestionRef.current.filesAttached,
                                              undefined,
                                              undefined,
                                              undefined,
                                              undefined,
                                              lastQuestionRef.current.templateId
                                          );
                                      }}
                                  />
                              </div>
                          </>
                      )
                    : null}

                <div ref={chatMessageStreamEnd} />
            </div>
        </div>
    );
};

export const ChatMessageList = memo(ChatMessageListComponent);
